import { useMemo } from 'react';

import { ProceduralUpload, formatBytes } from './use-procedural-upload';
import { ProceduralJurisdiction } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import { Icon } from '@/new/components/icon';
import { SearchableCombobox } from '@/new/components/searchable-combobox';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { detectTimeZone, getTimeZoneOptions } from '@/new/lib/time-zones';

const JURISDICTION_LABELS: Record<ProceduralJurisdiction, string> = {
  [ProceduralJurisdiction.EnglandAndWales]: 'England & Wales',
  [ProceduralJurisdiction.USFederal]: 'US Federal',
  [ProceduralJurisdiction.Other]: 'Other',
};

/**
 * The upload modal for a staged procedural timetable: file summary, timezone
 * and jurisdiction selection, duplicate/existing-event warnings, and submit.
 * Driven entirely by {@link useProceduralUpload}; shared by the page's upload
 * button and the overview-widget dropzone.
 */
export function ProceduralUploadDialog({ upload }: { upload: ProceduralUpload }) {
  const {
    selectedFile,
    timeZone,
    setTimeZone,
    jurisdiction,
    setJurisdiction,
    stagedUploadId,
    isStaging,
    isSubmitting,
    isBusy,
    stageError,
    submitLabel,
    duplicateName,
    hasExistingEvents,
    handleSubmit,
    cancelAndClear,
    handleOpenChange,
  } = upload;

  // Keep the current zone selectable even if the detected browser zone falls
  // outside the curated list.
  const timeZoneOptions = useMemo(() => getTimeZoneOptions(timeZone), [timeZone]);

  return (
    <Dialog open={selectedFile !== null} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Procedural Timetable</DialogTitle>
          <DialogDescription>
            Automatically extract procedural events from this document and propose them as suggestions for review.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          {selectedFile && (
            <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
              <Icon className="lucide--file-text text-gray-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-800">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">{formatBytes(selectedFile.size)}</p>
              </div>
              {isStaging && <Icon className="lucide--loader-2 animate-spin text-sky-600" />}
              {stagedUploadId && !isSubmitting && <Icon className="lucide--check-circle-2 text-teal-600" />}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="procedural-timezone" className="mb-0 text-xs font-medium text-gray-700">
              Time zone
            </Label>
            <p className="text-xs text-gray-500">Used to resolve dates the document expresses in local time.</p>
            <SearchableCombobox
              options={timeZoneOptions}
              value={timeZone}
              onChange={(v) => setTimeZone(v || detectTimeZone())}
              placeholder="Select time zone"
              searchPlaceholder="Search time zones..."
              emptyText="No time zones found."
              containerClassName="flex w-full items-center gap-2 [&>span]:w-full"
              triggerClassName="w-full justify-between font-normal"
              clearable={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="procedural-jurisdiction" className="mb-0 text-xs font-medium text-gray-700">
              Jurisdiction
            </Label>
            <p className="text-xs text-gray-500">
              Used to calculate court-day deadlines according to jurisdiction specific rules.
            </p>
            <Select value={jurisdiction} onValueChange={(v) => setJurisdiction(v as ProceduralJurisdiction)}>
              <SelectTrigger id="procedural-jurisdiction" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.values(ProceduralJurisdiction) as ProceduralJurisdiction[]).map((j) => (
                  <SelectItem key={j} value={j}>
                    {JURISDICTION_LABELS[j]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {jurisdiction === ProceduralJurisdiction.Other && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
                <p>
                  Automatic deadline calculations are only available for supported jurisdictions. Events will still be
                  extracted, but timeline rules may not be applied correctly. If you'd like us to add support for your
                  jurisdiction, email{' '}
                  <a href="mailto:support@wexler.ai" className="font-medium underline">
                    support@wexler.ai
                  </a>
                  .
                </p>
              </div>
            )}
          </div>

          {hasExistingEvents && (
            <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
              <Icon className="lucide--info mt-0.5 shrink-0 text-sky-600" />
              <p>
                Extracted events will be compared against your existing events, and where appropriate updates will be
                suggested for review rather than creating duplicates.
              </p>
            </div>
          )}

          {duplicateName !== null && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <Icon className="lucide--triangle-alert mt-0.5 shrink-0 text-amber-600" />
              <p>
                This file matches one already uploaded to this matter
                {duplicateName !== '' ? ` (“${duplicateName}”)` : ''}. You can upload it again, but it may produce
                duplicate events.
              </p>
            </div>
          )}

          {stageError && (
            <p className="text-sm text-red-600">
              Upload failed: {stageError.message}. Close this dialog and try again.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button onClick={cancelAndClear} disabled={isBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isBusy || !stagedUploadId || !!stageError}>
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
